/**
 * Trust Score Display Component
 * Shows user's trust score with level indicator and progress
 */

import React from 'react';
import {
    TrophyIcon,
    ShieldCheckIcon,
    StarIcon,
    UserPlusIcon,
    UserIcon
} from '@heroicons/react/24/outline';

import { TRUST_LEVELS } from '../../constants/verification';

interface TrustScoreDisplayProps {
    currentScore: number;
    level?: string;
    showProgress?: boolean;
    showBenefits?: boolean;
    size?: 'sm' | 'md' | 'lg';
    className?: string;
}

const TrustScoreDisplay: React.FC<TrustScoreDisplayProps> = ({
    currentScore,
    showProgress = true,
    showBenefits = false,
    size = 'md',
    className = ''
}) => {
    // Find current level configuration
    const currentLevel = TRUST_LEVELS.find(lvl =>
        currentScore >= lvl.minScore && currentScore <= lvl.maxScore
    ) || TRUST_LEVELS[0];

    // Find next level
    const nextLevel = TRUST_LEVELS.find(lvl => lvl.minScore > currentScore);

    // Calculate progress to next level
    const calculateProgress = () => {
        if (!nextLevel) return 100; // Max level reached

        const levelRange = nextLevel.minScore - currentLevel.minScore;
        const scoreProgress = currentScore - currentLevel.minScore;
        return Math.min(Math.max((scoreProgress / levelRange) * 100, 0), 100);
    };

    const progress = calculateProgress();

    // Size configurations
    const sizeConfig = {
        sm: {
            container: 'p-3',
            scoreText: 'text-lg font-bold',
            levelText: 'text-xs font-medium',
            progressBar: 'h-1',
            icon: 'w-4 h-4'
        },
        md: {
            container: 'p-4',
            scoreText: 'text-2xl font-bold',
            levelText: 'text-sm font-medium',
            progressBar: 'h-2',
            icon: 'w-5 h-5'
        },
        lg: {
            container: 'p-6',
            scoreText: 'text-3xl font-bold',
            levelText: 'text-base font-medium',
            progressBar: 'h-3',
            icon: 'w-6 h-6'
        }
    };

    const config = sizeConfig[size];

    // Get level icon
    const getLevelIcon = () => {
        const iconClass = `${config.icon} text-current`;

        switch (currentLevel.name.toLowerCase()) {
            case 'elite':
                return <TrophyIcon className={iconClass} />;
            case 'expert':
                return <ShieldCheckIcon className={iconClass} />;
            case 'advanced':
                return <StarIcon className={iconClass} />;
            case 'intermediate':
                return <UserPlusIcon className={iconClass} />;
            case 'beginner':
            default:
                return <UserIcon className={iconClass} />;
        }
    };

    // Get level color classes
    const getLevelColorClasses = () => {
        switch (currentLevel.name.toLowerCase()) {
            case 'elite':
                return 'text-yellow-600 dark:text-yellow-400';
            case 'expert':
                return 'text-purple-600 dark:text-purple-400';
            case 'advanced':
                return 'text-green-600 dark:text-green-400';
            case 'intermediate':
                return 'text-blue-600 dark:text-blue-400';
            case 'beginner':
            default:
                return 'text-neutral-600 dark:text-neutral-400';
        }
    };

    return (
        <div className={`card ${config.container} ${className}`}>
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div className={getLevelColorClasses()}>
                        {getLevelIcon()}
                    </div>
                    <div>
                        <div className={`${config.scoreText} text-neutral-900 dark:text-neutral-100`}>
                            {currentScore}
                        </div>
                        <div className={`${config.levelText} ${getLevelColorClasses()}`}>
                            {currentLevel.name} Level
                        </div>
                    </div>
                </div>

                <div className="text-right">
                    <div className="text-xs text-neutral-500 dark:text-neutral-400">
                        Trust Score
                    </div>
                    <div className="text-xs text-neutral-600 dark:text-neutral-300">
                        out of 100
                    </div>
                </div>
            </div>

            {/* Progress to next level */}
            {showProgress && nextLevel && (
                <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs text-neutral-600 dark:text-neutral-400">
                        <span>Progress to {nextLevel.name}</span>
                        <span>{Math.round(progress)}%</span>
                    </div>

                    <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
                        <div
                            className={`${config.progressBar} bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full transition-all duration-500`}
                            style={{ width: `${progress}%` }}
                        />
                    </div>

                    <div className="text-xs text-neutral-500 dark:text-neutral-400">
                        {nextLevel.minScore - currentScore} points to next level
                    </div>
                </div>
            )}

            {/* Current level benefits */}
            {showBenefits && currentLevel.benefits.length > 0 && (
                <div className="mt-4 pt-3 border-t border-neutral-200 dark:border-neutral-700">
                    <div className={`${config.levelText} text-neutral-700 dark:text-neutral-300 mb-2`}>
                        Current Benefits:
                    </div>
                    <ul className="space-y-1">
                        {currentLevel.benefits.map((benefit, index) => (
                            <li
                                key={index}
                                className="flex items-start gap-2 text-xs text-neutral-600 dark:text-neutral-400"
                            >
                                <div className="w-1 h-1 bg-primary-500 rounded-full mt-1.5 flex-shrink-0" />
                                <span>{benefit}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Next level preview */}
            {showBenefits && nextLevel && (
                <div className="mt-3 p-2 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
                    <div className="text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                        Unlock at {nextLevel.name} Level:
                    </div>
                    <div className="text-xs text-neutral-600 dark:text-neutral-400">
                        {nextLevel.benefits[0]}
                        {nextLevel.benefits.length > 1 && (
                            <span className="ml-1 text-primary-600 dark:text-primary-400">
                                +{nextLevel.benefits.length - 1} more
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrustScoreDisplay;